import { isEqual } from "lodash";

import { selectLayerByVersionAndLayerKey, selectLayerVersionImportStatus, useGetImportStatusQuery } from "fond/api";
import { ImportStatus, Layer } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

export type DesignLayers = Map<string, LayerStatus>;

export type LayerStatus = { layer: Layer | undefined; status: ImportStatus | undefined };

/**
 * Gets the layer and import status information for each of the layerIds passed.
 *
 * @param versionId the current versionId being requested
 * @returns A map containing the layer & import status of each imported Layer
 */
export const useGetImportLayers = (versionId: string, layerIds: string[]): DesignLayers => {
  useGetImportStatusQuery({ versionId });

  const layerStatus = useAppSelector((state) => {
    const map = new Map<string, LayerStatus>();

    layerIds.forEach((layerId) => {
      map.set(layerId, {
        layer: selectLayerByVersionAndLayerKey(state, { versionId, layerId }),
        status: selectLayerVersionImportStatus(state, { versionId, layerId }),
      });
    });

    return map;
  }, isEqual);

  return layerStatus;
};

/**
 * Gets the layer and import status information the layerId passed.
 *
 * @param versionId the current versionId being requested
 * @param layerId the id of the layer information to return
 * @returns LayerStatus for the layerId requested
 */
export const useGetImportLayer = (versionId: string, layerId: string): LayerStatus =>
  useAppSelector(
    (state) => ({
      layer: selectLayerByVersionAndLayerKey(state, { versionId, layerId }),
      status: selectLayerVersionImportStatus(state, { versionId, layerId }),
    }),
    isEqual
  );
